(function () {
  if (window.ModalProductFetch) return;

  const RESPONSE_CACHE = new Map();
  const PENDING_REQUESTS = new Map();
  const RESPONSE_CACHE_TTL_MS = 90 * 1000;
  const RESPONSE_CACHE_MAX_ENTRIES = 30;

  function normalizeProductUrl(productUrl) {
    const sourceUrl = new URL(productUrl, window.location.origin);
    const normalizedUrl = new URL(sourceUrl.pathname, sourceUrl.origin);
    const variant = sourceUrl.searchParams.get('variant');
    if (variant) normalizedUrl.searchParams.set('variant', variant);
    return normalizedUrl.toString();
  }

  function buildSectionFetchUrl(productUrl, sectionId) {
    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    return url.toString();
  }

  function getResponseTargets({ productUrl, fetchMode = 'section', sectionIds = [], includeProductPage }) {
    const normalizedProductUrl = normalizeProductUrl(productUrl);
    const targets = [];
    const shouldIncludeProductPage = typeof includeProductPage === 'boolean' ? includeProductPage : true;

    if (fetchMode === 'section') {
      sectionIds.forEach((sectionId) => {
        targets.push(buildSectionFetchUrl(normalizedProductUrl, sectionId));
      });
    }

    if (shouldIncludeProductPage) targets.push(normalizedProductUrl);
    return [...new Set(targets)];
  }

  function getCachedResponse(url) {
    const cacheEntry = RESPONSE_CACHE.get(url);
    if (!cacheEntry) return null;

    if (Date.now() - cacheEntry.timestamp > RESPONSE_CACHE_TTL_MS) {
      RESPONSE_CACHE.delete(url);
      return null;
    }

    return cacheEntry.text;
  }

  function cacheResponse(url, text) {
    RESPONSE_CACHE.set(url, {
      text,
      timestamp: Date.now(),
    });

    if (RESPONSE_CACHE.size <= RESPONSE_CACHE_MAX_ENTRIES) return;

    const oldestEntry = RESPONSE_CACHE.keys().next();
    if (!oldestEntry.done) RESPONSE_CACHE.delete(oldestEntry.value);
  }

  async function fetchResponseText(url) {
    const cachedResponse = getCachedResponse(url);
    if (cachedResponse !== null) return cachedResponse;

    const pendingRequest = PENDING_REQUESTS.get(url);
    if (pendingRequest) return pendingRequest;

    const requestPromise = fetch(url).then(async (response) => {
      if (!response.ok) throw new Error(`Modal product request failed: ${response.status}`);
      const responseText = await response.text();
      cacheResponse(url, responseText);
      return responseText;
    });

    PENDING_REQUESTS.set(url, requestPromise);
    return requestPromise.finally(() => {
      PENDING_REQUESTS.delete(url);
    });
  }

  function parseProductElement(responseText, validateProductElement) {
    if (!responseText || responseText === 'null') return null;

    const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const productElement = responseHTML.querySelector('product-info');
    if (!productElement) return null;

    if (typeof validateProductElement === 'function' && !validateProductElement(productElement)) {
      return null;
    }

    return productElement;
  }

  async function prefetchProductData({
    productUrl,
    fetchMode = 'section',
    sectionIds = [],
    validateProductElement,
    includeProductPage,
  }) {
    const targets = getResponseTargets({ productUrl, fetchMode, sectionIds, includeProductPage });

    for (const targetUrl of targets) {
      try {
        const responseText = await fetchResponseText(targetUrl);
        const productElement = parseProductElement(responseText, validateProductElement);
        if (productElement) return true;
      } catch (error) {
        // Ignore prefetch errors; interactive fetch will handle fallbacks.
      }
    }

    return false;
  }

  async function fetchProductElement({
    productUrl,
    fetchMode = 'section',
    sectionIds = [],
    validateProductElement,
    includeProductPage,
  }) {
    const targets = getResponseTargets({ productUrl, fetchMode, sectionIds, includeProductPage });

    for (const targetUrl of targets) {
      try {
        const responseText = await fetchResponseText(targetUrl);
        const productElement = parseProductElement(responseText, validateProductElement);
        if (productElement) return productElement;
      } catch (error) {
        // Continue with fallback targets.
      }
    }

    throw new Error('Modal product-info not found in response');
  }

  window.ModalProductFetch = {
    normalizeProductUrl,
    buildSectionFetchUrl,
    getResponseTargets,
    fetchResponseText,
    prefetchProductData,
    fetchProductElement,
  };
})();
